import React, { useState } from 'react';
import {
  Box,
  Button,
  Menu,
  MenuItem,
  CircularProgress,
  Typography,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useLocation } from '../../hooks/useLocation';

function LocationSelector() {
  const { selectedLocation, availableLocations, loading, switchLocation } = useLocation();
  const [anchorEl, setAnchorEl] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    locationId: null
  });

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLocationSelect = (location) => {
    const currentRoute = window.location.pathname;
    const editRoutes = ['/invoice', '/eyeexam', '/appointment', '/order'];
    const isInEditRoute = editRoutes.some(route => currentRoute.includes(route));

    if (isInEditRoute) {
      // Show confirmation dialog
      setConfirmDialog({
        open: true,
        locationId: location.id
      });
    } else {
      // Safe to switch directly
      switchLocation(location.id);
      handleClose();
    }
  };

  const handleConfirmSwitch = () => {
    if (confirmDialog.locationId) {
      switchLocation(confirmDialog.locationId);
    }
    setConfirmDialog({ open: false, locationId: null });
    handleClose();
  };

  const handleCancelSwitch = () => {
    setConfirmDialog({ open: false, locationId: null });
  };

  if (loading || !selectedLocation) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 2 }}>
        <CircularProgress size={20} sx={{ color: 'white' }} />
      </Box>
    );
  }

  return (
    <Box>
      <Button
        onClick={handleClick}
        startIcon={<LocationOnIcon />}
        endIcon={<ExpandMoreIcon />}
        sx={{
          color: 'white',
          textTransform: 'none',
          fontSize: '0.95rem',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.1)'
          }
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.25 }}>
          <Typography variant="caption" sx={{ opacity: 0.7 }}>
            Location
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {selectedLocation?.name || 'Select Location'}
          </Typography>
        </Box>
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}
      >
        <MenuItem disabled sx={{ opacity: 1 }}>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
            SELECT LOCATION
          </Typography>
        </MenuItem>
        <Divider sx={{ my: 0.5 }} />

        {availableLocations.map((location) => (
          <MenuItem
            key={location.id}
            onClick={() => handleLocationSelect(location)}
            selected={selectedLocation?.id === location.id}
            sx={{
              py: 1.5,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            <LocationOnIcon fontSize="small" />
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {location.name}
              </Typography>
              {location.address && (
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                  {location.address}
                </Typography>
              )}
            </Box>
            {selectedLocation?.id === location.id && (
              <Chip label="Current" size="small" color="primary" variant="outlined" />
            )}
          </MenuItem>
        ))}

        {availableLocations.length === 0 && (
          <MenuItem disabled>
            <Typography variant="body2">No locations available</Typography>
          </MenuItem>
        )}
      </Menu>

      {/* Confirmation Dialog for Edit Routes */}
      <Dialog open={confirmDialog.open} onClose={handleCancelSwitch} maxWidth="sm" fullWidth>
        <DialogTitle>Switch Location?</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2">
            You're editing something. Switching location will navigate away and discard unsaved changes.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={handleCancelSwitch} variant="outlined">
            Cancel
          </Button>
          <Button onClick={handleConfirmSwitch} variant="contained" color="primary">
            Switch Anyway
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default LocationSelector;
